import type { BlogStructuredContent } from "../types";

interface BlogRecipeSectionsProps {
  structuredContent: BlogStructuredContent;
  emptyMessage?: string;
}

function hasStructuredRecipeContent(structuredContent: BlogStructuredContent): boolean {
  return Boolean(
    structuredContent.servingText ||
      structuredContent.summaryTitle ||
      structuredContent.summaryBody ||
      structuredContent.stats.length > 0 ||
      structuredContent.ingredients.length > 0 ||
      structuredContent.directions.length > 0 ||
      structuredContent.notes.length > 0,
  );
}

export function BlogRecipeSections({
  structuredContent,
  emptyMessage = "Recipe details will appear here once they are added.",
}: BlogRecipeSectionsProps) {
  if (!hasStructuredRecipeContent(structuredContent)) {
    return (
      <div className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-4 py-5 text-sm text-[var(--pr-text-muted)]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {structuredContent.servingText || structuredContent.summaryTitle || structuredContent.summaryBody ? (
        <section className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-4 py-5 sm:px-5">
          {structuredContent.servingText ? (
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--pr-text-muted)]">
              {structuredContent.servingText}
            </p>
          ) : null}
          {structuredContent.summaryTitle ? (
            <h3 className="pr-display mt-2 text-2xl font-semibold">{structuredContent.summaryTitle}</h3>
          ) : null}
          {structuredContent.summaryBody ? (
            <p className="mt-3 max-w-[72ch] text-sm leading-relaxed text-[var(--pr-text-muted)]">
              {structuredContent.summaryBody}
            </p>
          ) : null}
        </section>
      ) : null}

      {structuredContent.stats.length > 0 ? (
        <section className="space-y-3">
          <div>
            <h3 className="text-base font-semibold">At a glance</h3>
            <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Quick reference metrics for this dietitian recommendation.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {structuredContent.stats.map((stat) => (
              <article
                key={stat.id}
                className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-4 py-4"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--pr-text-muted)]">{stat.label}</p>
                <p className="mt-2 text-2xl font-semibold">{stat.value}</p>
                {stat.secondaryValue ? <p className="mt-1 text-sm text-[var(--pr-text-muted)]">{stat.secondaryValue}</p> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {structuredContent.ingredients.length > 0 || structuredContent.directions.length > 0 ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <section className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-4 py-5 sm:px-5">
            <h3 className="text-base font-semibold">Ingredients</h3>
            {structuredContent.ingredients.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No ingredient sections were provided.</p>
            ) : (
              <div className="mt-4 space-y-4">
                {structuredContent.ingredients.map((section, sectionIndex) => (
                  <div key={section.id} className="space-y-2">
                    <h4 className="text-sm font-semibold">
                      {section.title || (structuredContent.ingredients.length > 1 ? `Section ${sectionIndex + 1}` : "Ingredients")}
                    </h4>
                    <ul className="space-y-2 text-sm leading-relaxed text-[var(--pr-text-muted)]">
                      {section.items.map((item, itemIndex) => (
                        <li key={`${section.id}-${itemIndex}`} className="flex gap-2">
                          <span aria-hidden="true" className="mt-1 text-[var(--pr-accent-deep)]">
                            •
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-4 py-5 sm:px-5">
            <h3 className="text-base font-semibold">Directions</h3>
            {structuredContent.directions.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--pr-text-muted)]">Directions will appear here when they are added.</p>
            ) : (
              <ol className="mt-4 space-y-4">
                {structuredContent.directions.map((direction, index) => (
                  <li key={direction.id} className="flex gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--pr-accent)_18%,transparent)] text-sm font-semibold text-[var(--pr-accent-deep)]">
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      {direction.title ? <h4 className="text-sm font-semibold">{direction.title}</h4> : null}
                      <p className="mt-1 text-sm leading-relaxed text-[var(--pr-text-muted)]">{direction.body}</p>
                      {direction.detail ? (
                        <p className="mt-2 text-xs font-medium uppercase tracking-[0.12em] text-[var(--pr-text-muted)]">
                          {direction.detail}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      ) : null}

      {structuredContent.notes.length > 0 ? (
        <section className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-4 py-5 sm:px-5">
          <h3 className="text-base font-semibold">Notes</h3>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-[var(--pr-text-muted)]">
            {structuredContent.notes.map((note, index) => (
              <li key={`${note}-${index}`} className="flex gap-2">
                <span aria-hidden="true" className="mt-1 text-[var(--pr-accent-deep)]">
                  •
                </span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
